import { camelCase } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { CircularProgress, MenuItem, TextField } from '@material-ui/core';
import { ConfigFieldControl, CoreEvent } from '@sierralabs/core-ui';
import { useEntityContext } from '@sierralabs/entity-ui';
import { EntityDataSource, Form, FormFilePlugin, FormMultiSelectPlugin, FormProvider, FormRadioGroupPlugin, useFindFormTemplate } from '@sierralabs/forms-ui';
import { useRunQuery } from '@sierralabs/query-ui';
import { useUserContext } from '@sierralabs/user-ui';
import { checkTaskFields, TaskEntityDataEvent, TaskEvent, TaskEventType, TaskStatusActionEvent, TaskTypePicker, useStateNodeByStatus, useTaskEntityData, useWorkflowItem, useWorkflowRequiredFields } from '@sierralabs/workflow-ui';
import SiteFormPicker from '../../common/pickers/SiteFormPicker';
import SiteRouteAndParamPicker, { updateRouteAndParam } from '../../common/pickers/SiteRouteAndParamPicker';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useSiteContext } from '../../SiteContext';
import { SiteComponent } from '../SiteComponentPlugin';
const StyledDiv = styled.div `
  padding: 24px;
  .loading {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .form-empty-state {
    border: 2px dotted ${p => p.theme.palette.primary.main};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 64px 24px;
  }
`;
export const SiteForm = props => {
    var _a;
    const component = useSubscribeComponentChanges(SiteFormPlugin, props);
    const { taskTypeId, formTemplateId, paramType, paramConfig, entityRouteParam } = component.props || {};
    const user = useUserContext().user;
    const { editing, formElementPlugins, isHover, hoverProps, paramPlugins } = useSiteContext();
    const { workflow } = useWorkflowItem({ taskTypeId }) || {};
    const params = useParams();
    const entityDataId = entityRouteParam ? params[entityRouteParam] : undefined;
    // make sure the number of called hooks are the same
    const hookResult = paramPlugins
        .map(plugin => {
        var _a;
        return ({
            type: plugin.type,
            hook: (_a = plugin.useHook) === null || _a === void 0 ? void 0 : _a.call(plugin)
        });
    })
        .find(result => result.type === paramType);
    const paramPlugin = paramPlugins.find(p => p.type === paramType);
    const formId = paramPlugin === null || paramPlugin === void 0 ? void 0 : paramPlugin.getParamValue(paramConfig, hookResult === null || hookResult === void 0 ? void 0 : hookResult.hook, undefined);
    const classes = ['site-form'];
    if (isHover(component)) {
        classes.push('component-hover');
    }
    // get form template
    const { formTemplate, isLoading, loadingError, findFormTemplate } = useFindFormTemplate(formTemplateId || 0);
    // get entity
    const { entities, loadingEntities } = useEntityContext();
    const entity = useMemo(() => entities === null || entities === void 0 ? void 0 : entities.find(
    // make sure formTemplate.entity.id is loaded
    e => { var _a; return e.id && ((_a = formTemplate === null || formTemplate === void 0 ? void 0 : formTemplate.entity) === null || _a === void 0 ? void 0 : _a.id) && e.id === formTemplate.entity.id; }), [entities, (_a = formTemplate === null || formTemplate === void 0 ? void 0 : formTemplate.entity) === null || _a === void 0 ? void 0 : _a.id]);
    // get task version based on the id param and configured target entity
    // use isLatest to disable fields and display the previous version data
    const { task, taskId, entityData, isLatest, getEntityDataById } = useTaskEntityData((entity === null || entity === void 0 ? void 0 : entity.id) || (entity === null || entity === void 0 ? void 0 : entity.tableName) || '', entityDataId);
    const stateNode = useStateNodeByStatus(task === null || task === void 0 ? void 0 : task.status, workflow);
    const { validate, setEventName } = useWorkflowRequiredFields(stateNode);
    const disableDataSources = useMemo(() => {
        const fields = ((entity === null || entity === void 0 ? void 0 : entity.columns) || []).map(column => column.name || camelCase(column.displayName));
        // disable all fields if viewing previous version
        if (!isLatest) {
            return fields;
        }
        const { disabled } = checkTaskFields(stateNode, user);
        return fields.filter(disabled);
    }, [entity, isLatest, stateNode, user]);
    useEffect(() => {
        if (formTemplateId)
            findFormTemplate();
    }, [findFormTemplate, formTemplateId]);
    // run query to get entity data
    const { queryData, runQueryGetOne } = useRunQuery();
    const runQuery = useCallback(() => {
        // query only if not viewing a previous version (isLatest)
        if (isLatest && entity && formId) {
            runQueryGetOne({
                queryType: 'select',
                selects: [],
                mainAlias: {
                    type: 'from',
                    name: entity.name,
                    tablePath: entity.tableName
                },
                wheres: [
                    {
                        type: 'simple',
                        condition: {
                            operator: 'equal',
                            parameters: ['id', formId]
                        }
                    }
                ]
            });
        }
    }, [formId, entity, isLatest, runQueryGetOne]);
    useEffect(() => runQuery(), [runQuery]);
    useEffect(() => {
        // refresh on change status
        // used to refresh data for ClearFields plugin
        return TaskEvent.handle(TaskEventType.ChangeStatus, () => {
            runQuery();
            getEntityDataById();
        });
    }, [getEntityDataById, runQuery]);
    useEffect(() => {
        // get required fields of specific event when TaskStatusAction gets clicked
        return CoreEvent.handle(TaskStatusActionEvent.TransitionAttempt, data => setEventName(data === null || data === void 0 ? void 0 : data.eventName));
    }, [setEventName]);
    useEffect(() => {
        // on task transition, make sure to clear eventName
        return CoreEvent.handle(TaskEventType.ChangeStatus, () => {
            setEventName(undefined);
        });
    }, [setEventName]);
    const data = isLatest ? queryData : entityData;
    return (React.createElement(SiteComponent, Object.assign({ plugin: SiteFormPlugin }, props),
        React.createElement(StyledDiv, Object.assign({ className: classes.join(' ') }, hoverProps(component)), formTemplate && entity ? (React.createElement(FormProvider, { entitySchema: entity, elementPlugins: formElementPlugins, formStateNode: formTemplate.config, disableDataSources: disableDataSources, getDataSourceProps: (element, value) => {
                const isValid = validate.field(element.dataSource || '', value);
                if (isValid) {
                    return {};
                }
                let helperText = 'Required Field';
                switch (element.type) {
                    case FormRadioGroupPlugin.type:
                    case FormMultiSelectPlugin.type:
                        helperText = 'At least one must be selected';
                        break;
                    case FormFilePlugin.type:
                        helperText = 'Attachment is required';
                        break;
                }
                return { error: !isValid, helperText };
            }, onChange: values => {
                // sendFormData(values as Partial<FormData<any>>);
                // console.log('form values', values);
            } },
            React.createElement(EntityDataSource, { formData: data, onChange: entityData => {
                    // dispatch event after form gets updated
                    CoreEvent.dispatch(TaskEntityDataEvent.Update, { taskId, entityData });
                } },
                React.createElement(Form, { values: data, 
                    // statePath={statePath}
                    onInit: formContext => {
                        // console.log('onInit Form', formContext);
                    }, onStateNodeChange: stateNodes => {
                        // get last state node
                        const stateNode = stateNodes[stateNodes.length - 1];
                        // if (stateNode) {
                        //   updateCompanyFormStatus({ stateNodePath: stateNode.path });
                        //   onChange?.(stateNode);
                        // }
                    } })))) : loadingEntities || isLoading ? (React.createElement("div", { className: 'loading' },
            React.createElement(CircularProgress, null))) : (editing && React.createElement("div", { className: 'form-empty-state' }, "No Form Selected")))));
};
export const SiteFormConfigurator = props => {
    var _a, _b, _c, _d, _e, _f;
    const { paramPlugins } = useSiteContext();
    const { component, remove, update } = useConfigComponent(props.component);
    const [componentProps, setComponentProps] = useState(component.props || {});
    const paramPlugin = paramPlugins.find(p => p.type === componentProps.paramType);
    const { formTemplate } = useFindFormTemplate(((_a = component.props) === null || _a === void 0 ? void 0 : _a.formTemplateId) || 0, true);
    const entityId = (_b = formTemplate === null || formTemplate === void 0 ? void 0 : formTemplate.entity) === null || _b === void 0 ? void 0 : _b.id;
    return (React.createElement(React.Fragment, null,
        React.createElement(ConfigFieldControl, null,
            React.createElement(SiteFormPicker, { value: (_c = component.props) === null || _c === void 0 ? void 0 : _c.formTemplateId, onChange: formTemplate => {
                    setComponentProps(Object.assign(Object.assign({}, componentProps), { formTemplateId: formTemplate.id }));
                    update('props.formTemplateId', formTemplate.id);
                } })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(TextField, { select: true, fullWidth: true, size: 'small', value: (componentProps === null || componentProps === void 0 ? void 0 : componentProps.paramType) || 'placeholder', label: 'Select Form ID Source', InputProps: { disableUnderline: true }, onChange: event => {
                    setComponentProps(Object.assign(Object.assign({}, componentProps), { paramType: event.target.value }));
                } },
                React.createElement(MenuItem, { disabled: true, value: 'placeholder', selected: !(componentProps === null || componentProps === void 0 ? void 0 : componentProps.paramType) }, "Form ID Source"),
                paramPlugins
                    .filter(p => p.type)
                    .map(plugin => (React.createElement(MenuItem, { key: plugin.type, value: plugin.type }, plugin.name))))),
        (paramPlugin === null || paramPlugin === void 0 ? void 0 : paramPlugin.configurator) && (React.createElement(ConfigFieldControl, null,
            React.createElement(paramPlugin.configurator, { config: componentProps.paramConfig, onChange: paramConfig => {
                    const props = Object.assign(Object.assign({}, componentProps), { paramConfig });
                    setComponentProps(props);
                    update('props', props);
                } }))),
        React.createElement(ConfigFieldControl, null,
            React.createElement(TaskTypePicker
            // re-render when entityId changes
            , { 
                // re-render when entityId changes
                key: entityId, value: (_d = component.props) === null || _d === void 0 ? void 0 : _d.taskTypeId, filter: { entityId }, textFieldProps: {
                    variant: undefined,
                    disabled: !entityId,
                    label: 'Task Type',
                    placeholder: 'Select Task Type',
                    InputProps: { disableUnderline: true }
                }, onChange: taskType => {
                    if (taskType) {
                        update('props.taskTypeId', taskType.id);
                    }
                    else {
                        remove('props.taskTypeId');
                    }
                } })),
        React.createElement(SiteRouteAndParamPicker, { nearest: true, component: component, routeId: (_e = component.props) === null || _e === void 0 ? void 0 : _e.entityRouteId, routeParam: (_f = component.props) === null || _f === void 0 ? void 0 : _f.entityRouteParam, siteRoutePickerProps: {
                label: 'Entity Detail Route',
                placeholder: 'Path for Entity detail page'
            }, onChange: value => {
                updateRouteAndParam(value, 'props.entityRouteId', 'props.entityRouteParam').forEach(([path, value]) => update(path, value));
            } })));
};
export const SiteFormPlugin = {
    name: 'Form',
    type: 'form',
    component: SiteForm,
    configurator: SiteFormConfigurator
};
//# sourceMappingURL=SiteFormPlugin.js.map